import { useState } from 'react';

import type { ActionIntent } from '../../services/agentDraftApi';

interface Funnel {
  id: string;
  name: string;
}

interface FunnelPickerMessageProps {
  funnels: Funnel[];
  allowCreate: boolean;
  onPick: (params: { displayMessage: string; intent: ActionIntent }) => void;
  onCreateNew?: (params: { displayMessage: string }) => void;
}

export function FunnelPickerMessage({
  funnels,
  allowCreate,
  onPick,
  onCreateNew,
}: FunnelPickerMessageProps) {
  const [selectedId, setSelectedId] = useState<string>('');

  const handlePick = () => {
    if (!selectedId) return;
    const funnel = funnels.find((f) => f.id === selectedId);
    if (!funnel) return;
    onPick({
      displayMessage: `Quero usar o funil "${funnel.name}"`,
      intent: { type: 'set_funnel', payload: { funnelId: selectedId } },
    });
  };

  const handleCreateNew = () => {
    if (!onCreateNew) return;
    onCreateNew({ displayMessage: 'Prefiro criar um funil novo do zero.' });
  };

  return (
    <div
      data-testid="funnel-picker-message"
      style={{
        marginTop: 8,
        padding: 12,
        background: '#f9fafb',
        borderRadius: 8,
        border: '1px solid #e5e7eb',
      }}
    >
      <p
        style={{
          margin: '0 0 10px',
          fontSize: 13,
          fontWeight: 600,
          color: '#1f2937',
        }}
      >
        Selecione um funil para gerenciar leads
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {funnels.map((funnel) => {
          const isSelected = selectedId === funnel.id;
          return (
            <button
              key={funnel.id}
              onClick={() => setSelectedId(funnel.id)}
              data-testid={`funnel-picker-option-${funnel.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 8,
                border: `2px solid ${isSelected ? '#8b5cf6' : '#e5e7eb'}`,
                background: isSelected ? '#f5f3ff' : '#fff',
                cursor: 'pointer',
                fontSize: 14,
                color: '#374151',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: `2px solid ${isSelected ? '#8b5cf6' : '#cbd5e1'}`,
                  background: isSelected ? '#8b5cf6' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {isSelected && (
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#fff',
                    }}
                  />
                )}
              </div>
              <span style={{ fontWeight: 600 }}>{funnel.name}</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handlePick}
          disabled={!selectedId}
          data-testid="funnel-picker-confirm"
          style={{
            padding: '7px 14px',
            borderRadius: 8,
            border: 'none',
            background: selectedId ? '#8b5cf6' : '#e5e7eb',
            color: selectedId ? '#fff' : '#9ca3af',
            fontWeight: 600,
            fontSize: 14,
            cursor: selectedId ? 'pointer' : 'not-allowed',
          }}
        >
          Usar este funil
        </button>
        {allowCreate && onCreateNew && (
          <button
            onClick={handleCreateNew}
            data-testid="funnel-picker-create-new"
            style={{
              padding: '7px 14px',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#fff',
              color: '#6b7280',
              fontWeight: 500,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Criar novo funil
          </button>
        )}
      </div>
    </div>
  );
}
